import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable, } from '@tanstack/react-table';
import { ButtonIcon, Localization, selectionElementColors } from '@sphereon/ui-components.core';
import SSITableViewHeader from './SSITableViewHeader';
import SSITypeLabel from '../../labels/SSITypeLabel';
import SSIHoverText from '../../fields/SSIHoverText';
import SSIStatusLabel from '../../labels/SSIStatusLabel';
import CredentialMiniCardView from '../CredentialMiniCardView';
import DropDownList from '../../lists/DropDownList';
import { SSITableViewCellContainerStyled as CellContainer, SSITableViewContainerStyled as Container, SSITableViewHeaderCellContainerStyled as HeaderCellContainer, SSITableViewLabelCellStyled as LabelCell, SSITableViewResultCountCaptionStyled as ResultCountCaption, SSITableViewHeaderRowContainerStyled as HeaderRowContainer, SSITableViewRowContainerStyled as RowContainer, SSITableViewTableContainerStyled as TableContainer, TableViewRowSelectionCheckboxContainerStyled as RowSelectionCheckboxContainer, } from '../../../styles';
import { TableCellType, } from '../../../types';
import PaginationControls from './PaginationControls';
import ComboBox from '../../fields/ComboBox';
import CredentialDetailsView from '../CredentialViewItem';
function IndeterminateCheckbox({ indeterminate, className = '', ...rest }) {
    const ref = React.useRef(null);
    React.useEffect(() => {
        if (typeof indeterminate === 'boolean') {
            ref.current.indeterminate = !rest.checked && indeterminate;
        }
    }, [ref, indeterminate]);
    return _jsx("input", { type: "checkbox", ref: ref, className: className + ' cursor-pointer', ...rest });
}
const getCellFormatting = (type, value, row, options) => {
    switch (type) {
        case TableCellType.TEXT:
            const { truncationLength, enableHover = false } = options ?? {};
            return _jsx(SSIHoverText, { text: value, truncationLength: truncationLength, enableHover: enableHover });
        case TableCellType.LABEL: {
            const labels = Array.isArray(value) ? value.map((label) => _jsx(SSITypeLabel, { type: label })) : _jsx(SSITypeLabel, { type: value });
            return _jsx(LabelCell, { children: labels });
        }
        case TableCellType.STATUS: {
            return _jsx(SSIStatusLabel, { status: value });
        }
        case TableCellType.CREDENTIAL_CARD: {
            return _jsx(CredentialMiniCardView, { ...value });
        }
        case TableCellType.CREDENTIAL_DETAILS: {
            return _jsx(CredentialDetailsView, { ...value, style: { maxWidth: 261 } });
        }
        case TableCellType.ACTIONS: {
            const { actions = [] } = options ?? {};
            const buttons = actions.map((action) => ({
                ...action,
                onClick: () => action.onClick(row),
            }));
            return _jsx(DropDownList, { icon: ButtonIcon.MEATBALLS, buttons: buttons, showBorder: true });
        }
        case TableCellType.COMBOBOX: {
            const { defaultValue, onChange, selectOptions = [] } = options ?? {};
            return _jsx(ComboBox, { defaultValue: defaultValue, value: value, onChange: onChange, options: selectOptions });
        }
        default:
            return _jsx("div", {});
    }
};
const toRowSelectionObject = (rows) => {
    const rowSelectionObject = {};
    rows.forEach((row) => {
        rowSelectionObject[row.rowId] = true;
    });
    return rowSelectionObject;
};
const SSITableView = (props) => {
    const { columns, data, enableRowSelection = false, enableRowHover = true, enableFiltering = false, enableMostRecent = false, enableResultCount = false, columnResizeMode = 'onChange', actions = [], onRowClick, onDelete, pagination, } = props;
    const [rowSelection, setRowSelection] = React.useState([]);
    const [focusedRowId, setFocusedRowId] = React.useState();
    const columnHelper = createColumnHelper();
    let availableColumns = columns.map((header) => columnHelper.accessor(header.accessor, {
        id: header.accessor,
        header: header.label,
        cell: (info) => getCellFormatting(header.type, info.getValue(), info.row, header.columnOptions?.cellOptions),
        minSize: header.columnOptions?.columnMinWidth,
        maxSize: header.columnOptions?.columnMaxWidth,
        size: header.columnOptions?.columnWidth,
    }));
    if (enableRowSelection) {
        availableColumns = [
            {
                id: 'select',
                header: ({ table }) => (_jsx(IndeterminateCheckbox, { ...{
                        checked: table.getIsAllRowsSelected(),
                        indeterminate: table.getIsSomeRowsSelected(),
                        onChange: table.getToggleAllRowsSelectedHandler(),
                    } })),
                cell: ({ row }) => (_jsx(RowSelectionCheckboxContainer, { children: (row.id === focusedRowId || rowSelection.length > 0) && (_jsx(IndeterminateCheckbox, { ...{
                            checked: row.getIsSelected(),
                            disabled: !row.getCanSelect(),
                            indeterminate: row.getIsSomeSelected(),
                            onChange: row.getToggleSelectedHandler(),
                        } })) })),
            },
            ...availableColumns,
        ];
    }
    const onFocusRow = (rowId) => {
        setFocusedRowId(rowId);
    };
    const onRowSelectionChange = (updatedRowSelection) => {
        const currentRowSelection = updatedRowSelection(toRowSelectionObject(rowSelection));
        const selection = Object.keys(currentRowSelection).map((key) => {
            return {
                rowId: key,
                rowData: data[Number(key)],
            };
        });
        setRowSelection(selection);
    };
    const table = useReactTable({
        defaultColumn: {
            size: 0,
        },
        state: {
            rowSelection: toRowSelectionObject(rowSelection),
        },
        enableRowSelection,
        onRowSelectionChange: onRowSelectionChange,
        data,
        columns: availableColumns,
        columnResizeMode,
        getCoreRowModel: getCoreRowModel(),
    });
    const onRowClicked = async (row) => {
        await onRowClick?.(row);
    };
    const onDeleteClicked = async () => {
        await onDelete?.(rowSelection.map((row) => row.rowData));
    };
    return (_jsxs(Container, { children: [_jsxs("div", { className: "overflow-x-auto", children: [enableResultCount && (_jsx(ResultCountCaption, { children: Localization.translate('result_count_label', {
                            count: data.length,
                            maxCount: data.length,
                        }) })), (enableFiltering || enableMostRecent || actions.length > 0) && (_jsx(SSITableViewHeader, { actions: actions, enableFiltering: enableFiltering, enableMostRecent: enableMostRecent, ...(onDelete && { onDelete: onDeleteClicked }) })), _jsxs(TableContainer, { children: [_jsx("thead", { children: table.getHeaderGroups().map((headerGroup) => (_jsx(HeaderRowContainer, { children: headerGroup.headers.map((header) => (_jsxs(HeaderCellContainer, { colSpan: header.colSpan, style: {
                                            ...(header.column.columnDef.minSize && { minWidth: header.column.columnDef.minSize }),
                                            ...(header.column.columnDef.maxSize && { maxWidth: header.column.columnDef.maxSize }),
                                            ...(header.column.columnDef.size !== 0 && { width: header.column.columnDef.size }),
                                        }, children: [header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext()), _jsx("div", { className: `resizer ${header.column.getIsResizing() ? 'isResizing' : ''}`, onMouseDown: header.getResizeHandler(), onTouchStart: header.getResizeHandler(), style: {
                                                    transform: columnResizeMode === 'onEnd' && header.column.getIsResizing()
                                                        ? `translateX(${table.getState().columnSizingInfo.deltaOffset}px)`
                                                        : '',
                                                } })] }, header.id))) }, headerGroup.id))) }), _jsx("tbody", { children: table.getRowModel().rows.map((row) => (_jsx(RowContainer, { enableHover: enableRowHover, onClick: () => onRowClicked(row), onMouseEnter: () => onFocusRow(row.id), onMouseLeave: () => onFocusRow(), style: { ...(row.getIsSelected() && { backgroundColor: selectionElementColors.selectedRow }) }, children: row.getVisibleCells().map((cell) => (_jsx(CellContainer, { style: {
                                            ...(cell.column.columnDef.minSize && { minWidth: cell.column.columnDef.minSize }),
                                            ...(cell.column.columnDef.maxSize && { maxWidth: cell.column.columnDef.maxSize }),
                                            ...(cell.column.columnDef.size !== 0 && { width: cell.column.columnDef.size }),
                                        }, children: flexRender(cell.column.columnDef.cell, cell.getContext()) }, cell.id))) }, row.id))) })] })] }), pagination && _jsx(PaginationControls, { ...pagination })] }));
};
export default SSITableView;
